import { useEffect, useRef, useState } from "react";
import { Link, useBlocker, useLocation, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useLocalBack } from "../navigation";
import { readWarehouseOperation } from "../warehouseOperation";
import { api, ApiError, type ReturnProjection, type ReturnSelection, type ReturnSource } from "../api";
import { useAuth } from "../auth";
import { InitialState, InlineError, dateTime, makeUuid, useResource } from "../components/DataState";
import { PageTitle } from "../components/Cards";
import { QuantityStepper } from "../components/QuantityStepper";
import { formatQuantity } from "../numberFormat";
import { t as translate } from "../preferences";
import {
  amount, cents, cleanDecimal, lineAmount, refundError, returnMoneyInput, editReturnMoney,
  readReturnDraft, writeReturnDraft, clearReturnDraft, reconcileReturnDraft, returnDraftTotal, returnBasisChanged,
  type ReturnDraft,
} from "../returnDraft";
import { saveNotice } from "../confirmationState";
import { useWarehouseOperation } from "../useWarehouseOperation";
import { PaymentFields } from "./WarehouseScreens";

type Mode = "purchase" | "sale";
const returnType = (mode: Mode) => mode === "purchase" ? "purchase_return" as const : "sale_return" as const;
const path = (mode: Mode) => `/mechanic/warehouse/${mode}-return`;
function title(mode: Mode) { return translate(mode === "purchase" ? "Xaridni qaytarish" : "Sotuvni qaytarish"); }
function stateLabel(state: ReturnSource["return_state"]) {
  return translate(state === "no_returns" ? "Qaytarilmagan" : state === "partially_returned" ? "Qisman qaytarilgan" : "To'liq qaytarilgan");
}

export function ReturnListScreen({ mode }: { mode: Mode }) {
  const [page, setPage] = useState(1);
  const resource = useResource(() => api.returnSources(returnType(mode), page), [mode, page]);
  const navigate = useNavigate();
  const sources = resource.data?.items.filter(doc => doc.can_return === true && doc.return_state !== "fully_returned") ?? [];
  // Only recovery of an existing frozen document-free request uses the old endpoint.
  const legacy = useWarehouseOperation(mode === "purchase" ? "purchase-return" : "sale-return", undefined,
    () => navigate("/mechanic/warehouse", { replace: true }));
  return <div className="m-space-y-4"><PageTitle title={title(mode)} />
    <InlineError message={legacy.error} onRetry={legacy.unknown ? legacy.retry : undefined} />
    <InitialState loading={resource.loading} error={resource.error} empty={Boolean(resource.data) && sources.length === 0} onRetry={() => void resource.retry()}>
      {resource.data ? <><div className="m-space-y-3">{sources.map((doc) => <article key={doc.original_document_id} className="m-rounded-2xl m-border m-border-border m-bg-surface m-p-4">
        <div className="m-flex m-justify-between m-gap-2"><strong>{translate("Hujjat")} #{doc.original_document_id}</strong><strong>{returnMoney(doc.original_total)}</strong></div>
        <p className="m-mt-1 m-text-sm m-text-muted">{dateTime(doc.created_at)}</p>
        <p className="m-mt-1 m-text-sm">{doc.counterparty_name ?? translate(mode === "purchase" ? "Yetkazib beruvchi ko'rsatilmagan" : "Anonim")}</p>
        <p className="m-mt-2 m-text-sm">{stateLabel(doc.return_state)} · {translate(doc.can_return ? "Qaytarish mumkin" : "Qaytariladigan miqdor yo'q")}</p>
        <div className="m-mt-3 m-flex m-gap-2"><Link className="m-secondary-button m-flex-1" to={`${path(mode)}/${doc.original_document_id}`}>{translate("Batafsil")}</Link>
          <button className="m-primary-button m-flex-1" disabled={!doc.can_return || legacy.saving || legacy.unknown} onClick={() => navigate(`${path(mode)}/${doc.original_document_id}`, { state: { returnAll: true } })}>{translate("Barchasini qaytarish")}</button></div>
      </article>)}</div>
      </> : null}
    </InitialState>
        {resource.data && resource.data.total > 20 ? <div className="m-flex m-items-center m-justify-between m-gap-2"><button className="m-secondary-button" disabled={page === 1 || resource.loading} onClick={() => setPage(page-1)}>{translate("Oldingi sahifa")}</button><span>{page} / {Math.ceil(resource.data.total/20)}</span><button className="m-secondary-button" disabled={page*20 >= resource.data.total || resource.loading} onClick={() => setPage(page+1)}>{translate("Keyingi sahifa")}</button></div> : null}
    </div>;
}

function returnMoney(value: string) { return returnMoneyInput(value) + " " + translate("so‘m"); }
const reviewMessage = () => translate("Miqdor yoki qarz o'zgardi. Hisob-kitobni tekshirib, qayta saqlang.");

export function ReturnDetailScreen({ mode }: { mode: Mode }) {
  const { documentId } = useParams();
  const id = Number(documentId);
  const location = useLocation(), navigate = useNavigate();
  const { state } = useAuth();
  if (state.status !== "authenticated" || state.user.workshop_id === null) throw new Error("Authentication required");
  const user = state.user, workshopId = user.workshop_id!;
  const type = returnType(mode);
  const [stored] = useState(() => readReturnDraft(user.id, workshopId, type, id));
  const kind = mode === "purchase" ? "purchase-document-return" : "sale-document-return";
  const [uuid] = useState(() => {
    const pending = readWarehouseOperation(user.id, workshopId);
    return (pending?.kind === kind && pending.resourceId === id ? pending.operationUuid : null) ?? stored?.operationUuid ?? makeUuid();
  });
  const [draft, setDraft] = useState<ReturnDraft | null>(null);
  const draftRef = useRef(draft);
  const [notice, setNotice] = useState<string | null>(null);
  const [storageWarning, setStorageWarning] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const preparingRef = useRef(false), mounted = useRef(true), allowNavigation = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const resource = useResource(async () => {
    if (!Number.isInteger(id) || id <= 0) throw new ApiError("document_not_found", translate("Hujjat topilmadi"), 404);
    const full = draftRef.current?.all ?? stored?.all ?? Boolean(location.state?.returnAll);
    return full ? api.previewReturn({ return_type: type, original_document_id: id, return_all: true })
      : api.returnability(type, id);
  }, [mode, id]);

  function commit(next: ReturnDraft) {
    draftRef.current = next; setDraft(next);
    if (next.dirty && !allowNavigation.current) setStorageWarning(!writeReturnDraft(next));
  }
  function initialDraft(basis: ReturnProjection, full: boolean): ReturnDraft {
    return {
      version: 1, userId: user.id, workshopId, returnType: type, sourceId: id, createdAt: Date.now(),
      operationUuid: uuid, quantities: Object.fromEntries(basis.items.map(item => [item.product_id, formatQuantity(item.selected_quantity)])),
      all: full, debt: basis.suggested_debt_reduction, cash: basis.suggested_cash_refund,
      payments: [], reason: "", basis, dirty: full, needsReview: false,
    };
  }
  function edit(next: ReturnDraft) {
    commit({ ...next, dirty: true, createdAt: draftRef.current?.dirty ? next.createdAt : Date.now() });
  }
  function acceptFresh(basis: ReturnProjection, forceReview = false) {
    const current = draftRef.current;
    if (current) {
      const next = reconcileReturnDraft(current, basis, forceReview);
      commit(next);
      if (next.needsReview) setNotice(reviewMessage());
    }
  }
  function handleConflict(error: unknown) {
    if (error instanceof ApiError && (error.code === "return_preview_changed" || error.code === "return_debt_changed")) {
      const fresh = error.details.projection as ReturnProjection | undefined;
      if (fresh) acceptFresh(fresh, true);
    }
  }
  const operation = useWarehouseOperation(kind, id,
    (result) => {
      // Only a confirmed outcome clears the draft. Frozen retry identity stays independent of its TTL.
      clearReturnDraft(user.id, workshopId, type, id, uuid);
      allowNavigation.current = true;
      navigate("/mechanic/warehouse", { replace: true, state: { noticeId: saveNotice(user, (result.ogohlantirishlar ?? []).join(" · "), true) } });
    }, handleConflict);
  const locked = operation.saving || operation.unknown || Boolean(operation.blocking?.kind === kind && operation.blocking.resourceId === id);
  const handledData = useRef<ReturnProjection | null>(null);
  useEffect(() => {
    if (!resource.data || allowNavigation.current || handledData.current === resource.data) return;
    handledData.current = resource.data;
    const current = draftRef.current;
    if (locked) {
      if (!current) commit(stored ?? initialDraft(resource.data, Boolean(location.state?.returnAll)));
      return;
    }
    if (!current) {
      const next = stored ? reconcileReturnDraft(stored, resource.data)
        : initialDraft(resource.data, Boolean(location.state?.returnAll));
      commit(next);
      if (next.needsReview) setNotice(reviewMessage());
    } else if (current.dirty) acceptFresh(resource.data);
    else commit(initialDraft(resource.data, current.all));
  }, [resource.data, locked]);

  const view = draft?.basis;
  const quantities = draft?.quantities ?? {}, debt = draft?.debt ?? "0.00", cash = draft?.cash ?? "0.00";
  const payments = draft?.payments ?? [];
  const total = draft ? returnDraftTotal(draft) : 0n;
  const cap = cents(view?.debt_reduction_max ?? "0") ?? 0n;
  const debtValue = cents(debt), cashValue = cents(cash);
  const settlementValid = debtValue !== null && cashValue !== null && debtValue + cashValue === total && debtValue <= cap;
  const refundValid = refundError(cashValue === 0n ? [] : payments, cash) === null;
  const hasQuantity = view?.items.some(item => Number(quantities[item.product_id]) > 0) ?? false;
  const busy = preparing || locked;
  const dirty = Boolean(draft?.dirty);
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    (dirty || busy) && !allowNavigation.current
      && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));
  useLocalBack(blocker.state === "blocked", () => { if (blocker.state === "blocked") blocker.reset(); });
  useEffect(() => {
    if (!dirty && !busy) return;
    const warn = (event: BeforeUnloadEvent) => { if (!allowNavigation.current) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, busy]);

  function changeQuantity(productId: number, quantity: string) {
    if (!draft || busy) return;
    const next = { ...draft, quantities: { ...quantities, [productId]: cleanDecimal(quantity, 3) }, all: false };
    const nextTotal = returnDraftTotal(next), reduction = nextTotal < cap ? nextTotal : cap;
    edit({ ...next, debt: amount(reduction), cash: amount(nextTotal - reduction), payments: [] });
    setNotice(translate("Miqdor o'zgarsa, hisob-kitob tavsiyasi yangilanadi. Summalarni tekshiring."));
  }
  function editSettlement(field: "debt" | "cash", raw: string) {
    if (!draft || busy || (field === "debt" && cap === 0n)) return;
    const value = editReturnMoney(raw, draft[field]), parsed = cents(value);
    const next = { ...draft, [field]: value, payments: [] };
    if (cap === 0n) next.debt = "0.00";
    else if (parsed !== null && parsed <= total) next[field === "debt" ? "cash" : "debt"] = amount(total - parsed);
    edit(next);
  }
  function selection(current: ReturnDraft, full = current.all): ReturnSelection {
    return { return_type: type, original_document_id: id, return_all: full,
      ...(full ? {} : { items: current.basis.items.filter(item => Number(current.quantities[item.product_id]) > 0)
        .map(item => ({ product_id: item.product_id, quantity: current.quantities[item.product_id] })) }) };
  }
  async function fillAll() {
    if (!draft || preparingRef.current || busy) return;
    preparingRef.current = true; setPreparing(true); operation.setError(null);
    try {
      const basis = await api.previewReturn(selection(draft, true));
      if (mounted.current) {
        edit({ ...initialDraft(basis, true), createdAt: draft.createdAt, reason: draft.reason });
        setNotice(null);
      }
    } catch (error) { if (mounted.current) operation.setError(error instanceof Error ? error.message : translate("So'rov bajarilmadi")); }
    finally { preparingRef.current = false; if (mounted.current) setPreparing(false); }
  }
  async function save() {
    if (!draft || busy || preparingRef.current || !settlementValid || !refundValid || !hasQuantity) return;
    preparingRef.current = true; setPreparing(true); operation.setError(null);
    try {
      const selected = selection(draft);
      const fresh = await api.previewReturn(selected);
      if (!mounted.current) return;
      if (returnBasisChanged(draft.basis, fresh) || (draft.all && fresh.preview_token !== draft.basis.preview_token)
        || cents(fresh.return_value) !== total || debtValue! > (cents(fresh.debt_reduction_max) ?? 0n)) {
        acceptFresh(fresh, true); return;
      }
      if (draft.needsReview) {
        commit({ ...draft, basis: fresh, needsReview: false });
      }
      const payload = { ...selected, reason: draft.reason.trim() || null, client_uuid: uuid,
        debt_reduction: amount(debtValue!), cash_refund: amount(cashValue!), expected_return_value: fresh.return_value,
        preview_token: fresh.preview_token,
        payments: cashValue! > 0n ? payments.map(line => ({ tolov_usuli: line.method, summa: amount(cents(line.amount)!) })) : [] };
      await operation.submit(payload, uuid);
    } catch (error) { if (mounted.current) { handleConflict(error); operation.setError(error instanceof Error ? error.message : translate("So'rov bajarilmadi")); } }
    finally { preparingRef.current = false; if (mounted.current) setPreparing(false); }
  }

  return <div className="m-space-y-4"><PageTitle title={title(mode)} />
    <InlineError viewport message={[operation.error, resource.error, draft?.needsReview ? notice : null, storageWarning ? translate("Qoralamani qurilmada saqlab bo‘lmadi. Sahifani yangilamang.") : null].filter(Boolean).join("\n") || null}
      onRetry={operation.unknown ? operation.retry : resource.error ? () => void resource.retry() : undefined} />
    {notice && !draft?.needsReview ? <p role="status" className="m-rounded-xl m-bg-elevated m-p-3 m-text-sm">{notice}</p> : null}
    <InitialState loading={resource.loading} error={null} onRetry={() => void resource.retry()}>{view && draft ? <fieldset disabled={busy} className="m-min-w-0 m-space-y-3">
      <div className="m-flex m-items-center m-justify-between m-gap-2"><div><strong>{translate("Hujjat")} #{id}</strong><p className="m-text-sm m-text-muted">{view.counterparty_name ?? translate("Anonim")}</p></div><button className="m-secondary-button" disabled={!view.can_return || busy} onClick={() => void fillAll()}>{translate("Barchasini qaytarish")}</button></div>
      {view.items.map(item => <article key={item.product_id} className="m-rounded-2xl m-border m-border-border m-bg-surface m-p-3">
        <strong>{item.product_nomi}</strong>
        <div className="m-mt-2 m-return-quantity-row"><span className="m-return-stock m-text-xs">{translate("Omborda:")} {formatQuantity(item.returnable_max)} {item.birlik}</span>
          <QuantityStepper inline label={translate("Qaytarish:")} value={quantities[item.product_id] ?? "0"} max={Number(item.returnable_max)} disabled={busy || Number(item.returnable_max) === 0} onChange={quantity => changeQuantity(item.product_id, quantity)} /></div>
        <p className="m-mt-2 m-text-sm m-text-muted">{returnMoney(amount(lineAmount(item, quantities[item.product_id] ?? "0")))}</p>
        {item.valuation_warning ? <p role="status" className="m-mt-2 m-text-xs m-text-muted">{translate("Oldingi qaytarish summasi asl qiymatdan oshgan. Qolgan tovar pulsiz qaytariladi.")}</p> : null}
      </article>)}
      <section className="m-rounded-2xl m-bg-elevated m-p-4"><div className="m-flex m-justify-between m-gap-2"><span>{translate("Qaytarish summasi")}</span><strong data-testid="return-total">{returnMoney(amount(total))}</strong></div>
        <p className="m-mt-2 m-text-xs m-text-muted">{translate(mode === "purchase" ? "Yetkazib beruvchidan qarzimiz" : "Mijoz qarzi:")} {returnMoney(view.debt_reduction_max)}</p>
        <label className="m-mt-3 m-block"><span className="m-field-label">{translate("Qarzdan kamaytirish")}</span><input className="m-text-input" inputMode="numeric" disabled={cap === 0n} value={returnMoneyInput(debt)} onChange={event => editSettlement("debt", event.target.value)} /></label>
        <label className="m-mt-3 m-block"><span className="m-field-label">{translate(mode === "purchase" ? "Qaytarib olinadigan pul" : "Qaytariladigan pul")}</span><input className="m-text-input" inputMode="numeric" value={returnMoneyInput(cash)} onChange={event => editSettlement("cash", event.target.value)} /></label>
        {!settlementValid ? <InlineError message={translate("Qarzdan kamaytirish va pul qaytarish summalarini tekshiring.")} /> : null}
      </section>
      {cashValue !== null && cashValue > 0n ? <PaymentFields total={Number(cash)} refundTotal={cash} payments={payments} setPayments={next => edit({ ...draft, payments: next })} showInlineErrors /> : null}
      <label className="m-block"><span className="m-field-label">{translate("Qaytarish sababi (ixtiyoriy)")}</span><textarea className="m-text-input m-min-h-20" value={draft.reason} onChange={event => edit({ ...draft, reason: event.target.value })} /></label>
      <button className="m-primary-button m-w-full" disabled={busy || !hasQuantity || !settlementValid || !refundValid} onClick={() => void save()}>{busy ? translate("Tekshirilmoqda…") : translate("Qaytarishni saqlash")}</button>
    </fieldset> : null}</InitialState>
    {blocker.state === "blocked" ? <div className="m-fixed m-inset-0 m-z-[80] m-flex m-items-center m-justify-center m-bg-black/45 m-p-4"><section className="m-w-full m-max-w-sm m-rounded-2xl m-bg-surface m-p-5 m-shadow-card" role="dialog" aria-modal="true" aria-labelledby="return-exit-title">
      <span className="m-flex m-size-12 m-items-center m-justify-center m-rounded-xl m-bg-elevated m-text-warning"><AlertTriangle className="m-size-6" /></span>
      <h2 id="return-exit-title" className="m-mt-4 m-text-xl m-font-black">{translate(busy ? "Saqlash natijasi tekshirilmoqda" : "Saqlanmagan o‘zgarishlar")}</h2>
      <p className="m-mt-2 m-text-sm m-leading-6 m-text-muted">{translate(busy ? "Takroriy qaytarish yaratilmasligi uchun avval shu saqlash natijasini aniqlang." : "Chiqishni tasdiqlaysizmi? Qaytarish qoralamasi 8 soat saqlanadi.")}</p>
      <div className="m-mt-5 m-space-y-2"><button className="m-primary-button" onClick={() => blocker.reset()}>{translate("Qolish")}</button>{!busy ? <button className="m-secondary-button" onClick={() => { allowNavigation.current = true; blocker.proceed(); }}>{translate("Chiqish")}</button> : null}</div>
    </section></div> : null}
  </div>;
}
